/**
 * RWBR — THE STAT BOARD, DERIVED.
 *
 * Howard, 18 Aug 2026:
 *
 *   "it says that the stats are there at the end of the season but that's not
 *    what I want. I want those stats updated every week and I want crazy
 *    thorough filterable sortable. I want so many stats it's crazy — at driver
 *    level and team level… at the season end you can summarize years, you can
 *    put the years together to track year over year, but this has to be running
 *    week over week statistics."
 *
 * ===========================================================================
 * WHY THIS FILE EXISTS AND WHAT IT IS FORBIDDEN TO DO
 * ===========================================================================
 *
 * The board had nothing to draw because the CAREER screen took a `CareerSeason`,
 * and a `CareerSeason` does not exist until the winter has been settled. A
 * season being played race by race is a `SeasonState`, and a `SeasonState`
 * already carries — has carried all along —
 *
 *     state.races               one SeasonRaceResult per race run
 *     state.standingsAfterRace  the whole championship, refolded after each one
 *     state.finance.events      the weekend's money, settled as it happens
 *
 * so "week over week" was never a missing calculation. It was a missing READER.
 * This module is that reader, and it reads BOTH shapes through one door
 * (`StatsSource`) so the identical table draws a season in progress and a
 * season that finished four winters ago.
 *
 * ⚠ NOTHING HERE DECIDES A RULE. Every championship number comes out of
 * `standings.ts`'s fold and every dollar out of `money.ts`'s. This file joins,
 * ranks, and derives ratios from numbers that already exist; the moment it
 * starts computing points, escape percentages or anything else the engine owns,
 * it is a second set of books and the project's governing rule —
 *
 *     "Season totals are a fold over the event stream, not a separate set of
 *      numbers that can drift out of sync with the race log."   — TELEMETRY_SPEC
 *
 * — has been broken by the screen that was supposed to display it.
 *
 * THE ONE THING THIS FILE DOES COMPUTE is arithmetic over engine outputs that
 * no engine module owns because no rule needs it: win percentage, points behind
 * the leader, a driver's rank LAST week against this week, dollars per start.
 * Every one of them is a ratio of two numbers that came from the fold, and
 * every one is recomputed on read. None is stored.
 *
 * ALL-TIME IS A MERGE OF SEASONS, NOT A LONGER SEASON. Counters add. Averages
 * re-weight by starts (`mergeAverage`) rather than averaging averages, which is
 * the classic way a career average start of 14.2 becomes 13.9 for no reason.
 * Percentages are recomputed from the merged numerator and denominator for the
 * same reason.
 */

import type { DriverCard } from '../domain/types.ts';
import type { Team } from '../domain/field.ts';
import type { ScheduledRace } from '../domain/schedule.ts';
import { SEASON_RACES } from '../domain/schedule.ts';
import type {
  DriverSeasonStats,
  SeasonEvent,
  SeasonStandings,
} from '../domain/standings.ts';
import { foldSeason } from '../domain/standings.ts';
import type { SeasonRaceResult, SeasonState } from '../domain/seasonPlay.ts';
import type { MoneyEvent } from '../domain/finance/money.ts';
import {
  CAREER_WINNINGS_CATEGORIES,
  accountKey,
  driverAccount,
  foldMoney,
} from '../domain/finance/money.ts';
import type { CareerSeason } from '../lib/careerRunner.ts';

// ---------------------------------------------------------------------------
// One door onto a season, however it was produced
// ---------------------------------------------------------------------------

/**
 * EVERYTHING THE BOARD NEEDS FROM A SEASON — and nothing that only one of the
 * two kinds of season has.
 *
 * A live `SeasonState` and a settled `CareerSeason` disagree about almost
 * everything structural: one has a winter, the other has a `nextRace`; one's
 * standings are final, the other's are the ones after the last flag. They agree
 * about all eight fields below, which is exactly why the board reads these and
 * not either of the two originals.
 */
export interface StatsSource {
  seasonNumber: number;
  seed: number;
  /** True once the year has been settled — a `CareerSeason` exists for it. */
  complete: boolean;
  /** Null until the last race of the playoff decides it. */
  championId: string | null;
  /** All 36, including the ones not yet run. The calendar, not the results. */
  schedule: readonly ScheduledRace[];
  races: readonly SeasonRaceResult[];
  /** Index i is the championship as it stood after race i+1. */
  standingsAfterRace: readonly SeasonStandings[];
  roster: readonly DriverCard[];
  teams: readonly Team[];
  /** Empty when the books were never turned on for this season. */
  moneyEvents: readonly MoneyEvent[];
}

/** The shape `App.tsx` holds a live season in (`lib/seasonSlot.ts`'s `LiveSeason`). */
export interface LiveSeasonLike {
  seasonNumber: number;
  roster: DriverCard[];
  teams: Team[];
  state: SeasonState;
}

/**
 * A season being played RIGHT NOW, through the same door as a finished one.
 *
 * `complete` is false even at race 36: a season is "complete" here when its
 * winter has been settled into a `CareerSeason`, because that is when the
 * champion, the money and the re-rates stop moving. A live state at race 36 has
 * run every race and still has a winter in front of it.
 */
export function fromLiveSeason(live: LiveSeasonLike): StatsSource {
  const { state } = live;
  return {
    seasonNumber: live.seasonNumber,
    seed: state.seed,
    complete: false,
    championId: null,
    schedule: state.schedule.races,
    races: state.races,
    standingsAfterRace: state.standingsAfterRace,
    // The roster as it stands — a rookie signed in the winter is in it, a dead
    // man is not. `state.roster` is the season's own list, in its own order.
    roster: state.roster,
    teams: live.teams,
    moneyEvents: state.finance?.events ?? [],
  };
}

/** A season that has been raced, paid and re-rated. */
export function fromCareerSeason(career: CareerSeason): StatsSource {
  return {
    seasonNumber: career.seasonNumber,
    seed: career.seed,
    complete: true,
    championId: career.season.championId,
    schedule: career.season.schedule.races,
    races: career.season.races,
    standingsAfterRace: career.season.standingsAfterRace,
    roster: career.roster,
    teams: career.teams,
    moneyEvents: career.finance.events,
  };
}

/**
 * Every season the shell can reach, oldest first, with the live one last.
 *
 * The live season is EXCLUDED when a settled `CareerSeason` already exists for
 * the same season number — that is the moment the winter lands and the two
 * describe the same year, and showing both would put season 3 in the picker
 * twice with slightly different money.
 */
export function collectSources(input: {
  history?: readonly CareerSeason[];
  career?: CareerSeason | null;
  live?: LiveSeasonLike | null;
}): StatsSource[] {
  const settled = [...(input.history ?? []), ...(input.career ? [input.career] : [])]
    .map(fromCareerSeason)
    .sort((a, b) => a.seasonNumber - b.seasonNumber);
  const seen = new Set(settled.map((s) => s.seasonNumber));
  const out = [...settled];
  if (input.live && !seen.has(input.live.seasonNumber)) out.push(fromLiveSeason(input.live));
  return out;
}

// ---------------------------------------------------------------------------
// A single race, for a single driver
// ---------------------------------------------------------------------------

export type RaceOutcome = 'ran' | 'dnq' | 'missed-the-show' | 'absent' | 'none';

export interface DriverRaceLine {
  raceNumber: number;
  outcome: RaceOutcome;
  /** 1-based grid slot. Null unless he started. */
  start: number | null;
  finish: number | null;
  points: number;
  turnsLed: number;
  ledMost: boolean;
  running: boolean;
  outReason: string | null;
  forfeited: boolean;
  /** Why he was not there — the absence reason, or the DNQ. */
  note: string | null;
  winningsMb: number;
  /** Championship position AFTER this race. */
  rankAfter: number | null;
  pointsAfter: number | null;
}

/** A column of the season grid: the calendar entry plus what happened in it. */
export interface RaceColumn {
  raceNumber: number;
  raceKey: string;
  raceName: string;
  trackId: string;
  trackName: string;
  location: string | null;
  category: string;
  round: string;
  doublePoints: boolean;
  /** False for a race still in front of the field. */
  run: boolean;
  winnerId: string | null;
  winnerName: string | null;
  poleId: string | null;
  poleName: string | null;
  cautions: number | null;
  leadChanges: number | null;
}

// ---------------------------------------------------------------------------
// Reading a modifier back off an event
// ---------------------------------------------------------------------------

function modNum(e: SeasonEvent, name: string): number | null {
  const found = e.modifiers.find((m) => m.name === name);
  if (!found) return null;
  return typeof found.value === 'number' ? found.value : Number(found.value) || 0;
}

function modStr(e: SeasonEvent, name: string): string | null {
  const found = e.modifiers.find((m) => m.name === name);
  return found && typeof found.value === 'string' ? found.value : null;
}

// ---------------------------------------------------------------------------
// A driver's row
// ---------------------------------------------------------------------------

/**
 * ONE DRIVER, EVERYTHING KNOWN ABOUT HIM.
 *
 * Every field down to `feudRaces` is `DriverSeasonStats` verbatim — the fold's
 * own numbers, not copies with a different name — and everything below it is a
 * ratio or a join computed here. Keeping the engine's names is deliberate: a
 * column header that says RAF% and a field called `rafPct` can be traced to
 * `standings.ts` by anyone reading either one.
 */
export interface StatRow extends DriverSeasonStats {
  carNumber: string;
  teamName: string;
  /** How many seasons contributed. 1 for a single season, N for all-time. */
  seasons: number;
  championships: number;

  // ---- derived: rates ----------------------------------------------------
  winPct: number | null;
  top5Pct: number | null;
  top10Pct: number | null;
  finishPct: number | null;
  /** Races led / starts. */
  ledPct: number | null;
  duelWinPct: number | null;
  /** Turns led per start. A turn is 10 miles (p.ii), not a lap. */
  turnsLedPerStart: number | null;

  // ---- derived: the championship, in context -----------------------------
  /** Points behind the leader of this table. 0 for the leader. */
  pointsBehind: number;
  /** Points behind the man one place ahead. 0 for the leader. */
  pointsBehindNext: number;
  /**
   * Championship places gained (+) or lost (−) since the race before last.
   * Null when there is nothing to compare against — race 1, or an all-time
   * table, where "last week" does not mean anything.
   */
  rankDelta: number | null;
  /** Rank after the previous race. Null on race 1 and in all-time. */
  rankBefore: number | null;

  // ---- derived: form -----------------------------------------------------
  /** Finishes in the most recent races, newest last. Absences omitted. */
  lastFive: number[];
  /** Consecutive races finished in the top ten, counting back from the last. */
  top10Streak: number;
  /** Consecutive races started. Broken by a DNQ or an absence. */
  startStreak: number;
  /** Race number of his most recent win. Null if he has not won. */
  lastWinRace: number | null;

  // ---- money -------------------------------------------------------------
  /** Purse and contingency only — money won racing (`money.ts`). */
  winningsMb: number;
  winningsPerStartMb: number | null;
  incomeMb: number;
  expenseMb: number;
  balanceMb: number;

  /** Race number → what he did in it. Empty in the all-time table. */
  byRace: Record<number, DriverRaceLine>;
}

export interface TeamRow {
  teamId: string;
  name: string;
  driverIds: string[];
  /** The shop's base paint, for the swatch beside its name. */
  baseColor: string | null;
  seasons: number;

  points: number;
  starts: number;
  entries: number;
  wins: number;
  poles: number;
  top5: number;
  top10: number;
  dnfs: number;
  racesFinished: number;
  turnsLed: number;
  racesLed: number;
  dnqs: number;
  heatIncidents: number;
  troubleAttempts: number;
  troubleEscapes: number;

  avgStart: number | null;
  avgFinish: number | null;
  bestFinish: number | null;
  winPct: number | null;
  top5Pct: number | null;
  finishPct: number | null;
  escapePct: number | null;
  pointsPerStart: number | null;

  winningsMb: number;
  /** The shop's drivers, in championship order. */
  drivers: StatRow[];
  rank: number;
  /** Championship places gained or lost since the previous race. */
  rankDelta: number | null;
}

/** One driver's line for one season — the year-over-year grid's cell. */
export interface SeasonLine {
  seasonNumber: number;
  rank: number;
  points: number;
  wins: number;
  top5: number;
  top10: number;
  starts: number;
  avgFinish: number | null;
  winningsMb: number;
  champion: boolean;
}

/** Everything one table needs, for one season or for all of them. */
export interface StatBoard {
  /** The season number, or null for the all-time table. */
  seasonNumber: number | null;
  label: string;
  complete: boolean;
  /** Races run. 0 before the first flag. */
  throughRace: number;
  totalRaces: number;
  championId: string | null;
  drivers: StatRow[];
  teams: TeamRow[];
  /** The calendar, in order. Empty in the all-time table. */
  races: RaceColumn[];
  /** Every season that fed this board, oldest first. */
  seasonNumbers: number[];
  /**
   * driverId → one line per season he raced, oldest first. Populated only on
   * the all-time board, where it is the year-over-year grid; a single season
   * already has `byRace`, which is the same question asked of weeks.
   */
  perSeason: Record<string, SeasonLine[]>;
}

// ---------------------------------------------------------------------------
// Building one season's board
// ---------------------------------------------------------------------------

const EMPTY_STANDINGS: SeasonStandings = foldSeason([]);

/** Winnings, by driver and by race. Purse and contingency only, income only. */
function winningsIndex(events: readonly MoneyEvent[]): {
  byDriver: Record<string, number>;
  byDriverRace: Record<string, Record<number, number>>;
} {
  const byDriver: Record<string, number> = {};
  const byDriverRace: Record<string, Record<number, number>> = {};
  for (const e of events) {
    if (e.account.kind !== 'driver') continue;
    if (!CAREER_WINNINGS_CATEGORIES.includes(e.category)) continue;
    if (e.amountMb <= 0) continue;
    const id = e.account.id;
    byDriver[id] = (byDriver[id] ?? 0) + e.amountMb;
    if (e.raceNumber !== null) {
      (byDriverRace[id] ??= {})[e.raceNumber] =
        (byDriverRace[id]?.[e.raceNumber] ?? 0) + e.amountMb;
    }
  }
  return { byDriver, byDriverRace };
}

/**
 * EVERY RACE, EVERY DRIVER — the grid behind the season matrix.
 *
 * Built off each race's OWN slice of the ledger and its OWN standings, both of
 * which `runSeasonRace` produced at the time. Nothing is recomputed and nothing
 * is inferred from the race after it, so a driver who was suspended for race 12
 * has a line for race 12 saying so rather than a hole the reader has to guess at.
 */
function raceLines(
  source: StatsSource,
  winningsByDriverRace: Record<string, Record<number, number>>,
): Record<string, Record<number, DriverRaceLine>> {
  const out: Record<string, Record<number, DriverRaceLine>> = {};
  const put = (driverId: string, line: DriverRaceLine) => {
    (out[driverId] ??= {})[line.raceNumber] = line;
  };

  for (const race of source.races) {
    const n = race.raceNumber;
    const after = race.standings;
    const money = (id: string) => winningsByDriverRace[id]?.[n] ?? 0;
    const rank = (id: string) => after.byDriver[id]?.rank ?? null;
    const pts = (id: string) => after.byDriver[id]?.points ?? null;
    const scored = (id: string) => after.byDriver[id]?.pointsByRace[n] ?? 0;

    // Entries first, so a DNQ has a line even though he has no result event.
    for (const e of race.events) {
      if (e.momentTag !== 'season.entry') continue;
      const driverId = e.actorIds[0];
      if (!driverId) continue;
      const status = modStr(e, 'entry-status') ?? 'start';
      if (status === 'start') continue; // the result event below carries these
      put(driverId, {
        raceNumber: n,
        outcome: status === 'dnq' ? 'dnq' : 'missed-the-show',
        start: null,
        finish: null,
        points: 0,
        turnsLed: 0,
        ledMost: false,
        running: false,
        outReason: null,
        forfeited: false,
        note: status === 'dnq' ? 'failed to qualify' : 'left on the bubble',
        winningsMb: money(driverId),
        rankAfter: rank(driverId),
        pointsAfter: pts(driverId),
      });
    }

    for (const e of race.events) {
      if (e.momentTag !== 'season.result') continue;
      const driverId = e.actorIds[0];
      if (!driverId) continue;
      put(driverId, {
        raceNumber: n,
        outcome: 'ran',
        start: race.startPositions[driverId] ?? null,
        finish: modNum(e, 'finish-position'),
        points: scored(driverId),
        turnsLed: modNum(e, 'turns-led') ?? 0,
        ledMost: (modNum(e, 'led-most-turns') ?? 0) > 0,
        running: (modNum(e, 'running-at-finish') ?? 0) > 0,
        outReason: modStr(e, 'out-reason'),
        forfeited: (modNum(e, 'points-forfeited') ?? 0) > 0,
        note: null,
        winningsMb: money(driverId),
        rankAfter: rank(driverId),
        pointsAfter: pts(driverId),
      });
    }

    for (const absence of race.absences) {
      put(absence.driverId, {
        raceNumber: n,
        outcome: 'absent',
        start: null,
        finish: null,
        points: 0,
        turnsLed: 0,
        ledMost: false,
        running: false,
        outReason: null,
        forfeited: false,
        note: absence.note || absence.reason,
        winningsMb: 0,
        rankAfter: rank(absence.driverId),
        pointsAfter: pts(absence.driverId),
      });
    }
  }
  return out;
}

/**
 * A PERCENTAGE, 0–100, for the fields THIS MODULE invents.
 *
 * ⚠ THE ENGINE'S OWN TWO ARE NOT THESE. `DriverSeasonStats.rafPct` and
 * `.escapePct` are FRACTIONS (0–1) despite their names — `standings.ts` line
 * 677 divides and stops, because SAG/8 reads the escape figure straight back as
 * next season's ESCAPE rating and a rating is not a percentage. Every inherited
 * field on `StatRow` therefore keeps the engine's units, and the two fractions
 * are rendered by a formatter that knows it (`fracPct` in `StatsBoard.tsx`).
 *
 * The all-time merge below RECOMPUTES both of them, and it recomputes them as
 * fractions for exactly this reason. Getting that wrong would have shown a
 * season escape rate of 71% beside a career escape rate of 0.7% on the same
 * screen, which is the kind of bug that looks like a rendering glitch and is
 * actually two different meanings sharing one name. `statsModel.test.ts` pins
 * it.
 */
const pct = (num: number, den: number): number | null => (den > 0 ? (num / den) * 100 : null);

/** The engine's units: a fraction, 0–1. */
const frac = (num: number, den: number): number | null => (den > 0 ? num / den : null);

/** The form fields — everything that means "lately" rather than "this year". */
function form(lines: Record<number, DriverRaceLine>, through: number) {
  const ran = Object.values(lines)
    .filter((l) => l.outcome === 'ran' && l.finish !== null)
    .sort((a, b) => a.raceNumber - b.raceNumber);
  const lastFive = ran.slice(-5).map((l) => l.finish as number);

  let top10Streak = 0;
  for (let i = ran.length - 1; i >= 0; i -= 1) {
    if ((ran[i].finish as number) <= 10) top10Streak += 1;
    else break;
  }

  let startStreak = 0;
  for (let n = through; n >= 1; n -= 1) {
    const line = lines[n];
    if (line && line.outcome === 'ran') startStreak += 1;
    else if (line) break;
    else break;
  }

  const wins = ran.filter((l) => l.finish === 1);
  return {
    lastFive,
    top10Streak,
    startStreak,
    lastWinRace: wins.length > 0 ? wins[wins.length - 1].raceNumber : null,
  };
}

/**
 * ONE SEASON, ASSEMBLED.
 *
 * `standings` is the LAST fold this season produced — after the most recent
 * race for a live season, final for a settled one — and `previous` is the one
 * before it, which is the only thing week-over-week movement can be measured
 * against.
 */
export function seasonBoard(source: StatsSource): StatBoard {
  const through = source.standingsAfterRace.length;
  const standings = source.standingsAfterRace[through - 1] ?? EMPTY_STANDINGS;
  const previous = through >= 2 ? source.standingsAfterRace[through - 2] : null;

  const ledger = foldMoney(source.moneyEvents);
  const { byDriver: winnings, byDriverRace } = winningsIndex(source.moneyEvents);
  const lines = raceLines(source, byDriverRace);

  const cardOf = new Map(source.roster.map((c) => [c.driverId, c]));
  const teamOf = new Map<string, Team>();
  for (const team of source.teams) for (const id of team.driverIds) teamOf.set(id, team);

  const leaderPoints = standings.drivers[0]?.points ?? 0;

  const drivers: StatRow[] = standings.drivers.map((d, i) => {
    const card = cardOf.get(d.driverId);
    const team = teamOf.get(d.driverId) ?? source.teams.find((t) => t.teamId === d.teamId) ?? null;
    const byRace = lines[d.driverId] ?? {};
    const book = ledger.books[accountKey(driverAccount(d.driverId))];
    const f = form(byRace, through);
    const ahead = standings.drivers[i - 1];
    return {
      ...d,
      carNumber: card?.carNumber ?? '',
      teamName: team?.name ?? 'independent',
      seasons: 1,
      championships: source.championId === d.driverId ? 1 : 0,

      winPct: pct(d.wins, d.starts),
      top5Pct: pct(d.top5, d.starts),
      top10Pct: pct(d.top10, d.starts),
      finishPct: pct(d.racesFinished, d.starts),
      ledPct: pct(d.racesLed, d.starts),
      duelWinPct: pct(d.duelsWon, d.duelsWon + d.duelsLost),
      turnsLedPerStart: d.starts > 0 ? d.turnsLed / d.starts : null,

      pointsBehind: leaderPoints - d.points,
      pointsBehindNext: ahead ? ahead.points - d.points : 0,
      rankBefore: previous?.byDriver[d.driverId]?.rank ?? null,
      rankDelta: previous?.byDriver[d.driverId]
        ? (previous.byDriver[d.driverId] as DriverSeasonStats).rank - d.rank
        : null,

      ...f,

      winningsMb: winnings[d.driverId] ?? 0,
      winningsPerStartMb: d.starts > 0 ? (winnings[d.driverId] ?? 0) / d.starts : null,
      incomeMb: book?.incomeMb ?? 0,
      expenseMb: book?.expenseMb ?? 0,
      balanceMb: book?.balanceMb ?? 0,

      byRace,
    };
  });

  return {
    seasonNumber: source.seasonNumber,
    label: `Season ${source.seasonNumber}`,
    complete: source.complete,
    throughRace: through,
    totalRaces: source.schedule.length || SEASON_RACES,
    championId: source.championId,
    drivers,
    teams: teamRows(drivers, source, standings, previous),
    races: raceColumns(source, standings),
    seasonNumbers: [source.seasonNumber],
    perSeason: {},
  };
}

/** The calendar, with the result of every race that has been run. */
function raceColumns(source: StatsSource, standings: SeasonStandings): RaceColumn[] {
  const byNumber = new Map(source.races.map((r) => [r.raceNumber, r]));
  const nameOf = (id: string | null) =>
    id ? (standings.byDriver[id]?.name ?? source.roster.find((c) => c.driverId === id)?.name ?? id) : null;

  return source.schedule.map((s) => {
    const run = byNumber.get(s.raceNumber);
    const poleId =
      run
        ? (Object.entries(run.startPositions).find(([, slot]) => slot === 1)?.[0] ?? null)
        : null;
    return {
      raceNumber: s.raceNumber,
      raceKey: s.raceKey,
      raceName: s.raceName,
      trackId: s.trackId,
      trackName: s.trackName,
      location: s.location,
      category: s.category,
      round: s.round,
      doublePoints: s.doublePoints,
      run: Boolean(run),
      winnerId: run?.race.winnerId ?? null,
      winnerName: nameOf(run?.race.winnerId ?? null),
      poleId,
      poleName: nameOf(poleId),
      cautions: run ? run.race.cautions.length : null,
      leadChanges: run ? run.race.leadChanges.length : null,
    } as RaceColumn;
  });
}

/**
 * THE SHOPS.
 *
 * Built off the GARAGE's teams (`field.ts`) rather than off the fold's
 * `TeamSeasonStats`, for one reason that matters on screen: the fold keys a
 * team by the id it read off a driver's card, and a shop with no starters this
 * week would simply be absent. A garage that loses a row when its cars are
 * parked is not a garage. Every chartered shop appears, every week, even at
 * zero.
 */
function teamRows(
  drivers: StatRow[],
  source: StatsSource,
  standings: SeasonStandings,
  previous: SeasonStandings | null,
): TeamRow[] {
  const byId = new Map(drivers.map((d) => [d.driverId, d]));

  const rows: TeamRow[] = source.teams.map((team) => {
    const members = team.driverIds.map((id) => byId.get(id)).filter((d): d is StatRow => Boolean(d));
    const sum = (pick: (d: StatRow) => number) => members.reduce((a, d) => a + pick(d), 0);
    const starts = sum((d) => d.starts);
    const points = sum((d) => d.points);
    const wins = sum((d) => d.wins);
    const finished = sum((d) => d.racesFinished);
    const attempts = sum((d) => d.troubleAttempts);
    const escapes = sum((d) => d.troubleEscapes);
    const bests = members.map((d) => d.bestFinish).filter((v): v is number => v !== null);

    return {
      teamId: team.teamId,
      name: team.name,
      driverIds: [...team.driverIds],
      baseColor: team.livery?.base ?? null,
      seasons: 1,
      points,
      starts,
      entries: sum((d) => d.entries),
      wins,
      poles: sum((d) => d.poles),
      top5: sum((d) => d.top5),
      top10: sum((d) => d.top10),
      dnfs: sum((d) => d.dnfs),
      racesFinished: finished,
      turnsLed: sum((d) => d.turnsLed),
      racesLed: sum((d) => d.racesLed),
      dnqs: sum((d) => d.dnqs),
      heatIncidents: sum((d) => d.heatIncidents),
      troubleAttempts: attempts,
      troubleEscapes: escapes,
      avgStart: mergeAverage(members.map((d) => [d.avgStart, d.starts])),
      avgFinish: mergeAverage(members.map((d) => [d.avgFinish, d.starts])),
      bestFinish: bests.length > 0 ? Math.min(...bests) : null,
      winPct: pct(wins, starts),
      top5Pct: pct(sum((d) => d.top5), starts),
      finishPct: pct(finished, starts),
      escapePct: pct(escapes, attempts),
      pointsPerStart: starts > 0 ? points / starts : null,
      winningsMb: sum((d) => d.winningsMb),
      drivers: members.sort((a, b) => a.rank - b.rank),
      rank: 0,
      rankDelta: null,
    };
  });

  rows.sort((a, b) => b.points - a.points || b.wins - a.wins || (a.avgFinish ?? 99) - (b.avgFinish ?? 99));
  rows.forEach((r, i) => {
    r.rank = i + 1;
  });

  // Week over week for a SHOP is the same question asked of the same numbers a
  // race earlier: rank the previous fold's team points the same way and take
  // the difference. Ranked here rather than remembered, because a rank that is
  // stored is a rank that can be stale.
  if (previous) {
    const before = source.teams
      .map((team) => ({
        teamId: team.teamId,
        points: team.driverIds.reduce(
          (a, id) => a + (previous.byDriver[id]?.points ?? 0),
          0,
        ),
        wins: team.driverIds.reduce((a, id) => a + (previous.byDriver[id]?.wins ?? 0), 0),
      }))
      .sort((a, b) => b.points - a.points || b.wins - a.wins);
    const rankBefore = new Map(before.map((t, i) => [t.teamId, i + 1]));
    for (const row of rows) {
      const was = rankBefore.get(row.teamId);
      row.rankDelta = was === undefined ? null : was - row.rank;
    }
  }
  void standings;
  return rows;
}

// ---------------------------------------------------------------------------
// All-time
// ---------------------------------------------------------------------------

/**
 * Re-weight averages by the number of starts each contributed.
 *
 * A career average finish is NOT the average of nine season averages: a year in
 * which a man started four races does not count as much as one in which he
 * started thirty-six, and averaging the averages says it does. Pairs whose
 * average is null (he never started) contribute nothing at all rather than a
 * zero, which would drag the answer toward the pit lane.
 */
export function mergeAverage(pairs: readonly (readonly [number | null, number])[]): number | null {
  let weight = 0;
  let total = 0;
  for (const [value, n] of pairs) {
    if (value === null || n <= 0) continue;
    total += value * n;
    weight += n;
  }
  return weight > 0 ? total / weight : null;
}

/**
 * EVERY SEASON, ADDED UP — the year-over-year table.
 *
 * Counters add. Averages re-weight. Percentages are recomputed from the merged
 * numerator and denominator. Ranks are thrown away and re-earned: a man who was
 * 3rd, 1st and 9th is not "13th all time", he is however he places on career
 * points against everybody else in this table.
 */
export function allTimeBoard(sources: readonly StatsSource[]): StatBoard {
  const boards = sources.map(seasonBoard);
  const merged = new Map<string, StatRow>();
  const perSeason = new Map<string, { seasonNumber: number; row: StatRow }[]>();

  for (const board of boards) {
    for (const row of board.drivers) {
      perSeason.set(row.driverId, [
        ...(perSeason.get(row.driverId) ?? []),
        { seasonNumber: board.seasonNumber as number, row },
      ]);
      const at = merged.get(row.driverId);
      merged.set(row.driverId, at ? addRows(at, row) : { ...row, byRace: {} });
    }
  }

  const lines: Record<string, SeasonLine[]> = {};
  for (const [driverId, entries] of perSeason) {
    lines[driverId] = entries
      .map(({ seasonNumber, row: r }) => ({
        seasonNumber,
        rank: r.rank,
        points: r.points,
        wins: r.wins,
        top5: r.top5,
        top10: r.top10,
        starts: r.starts,
        avgFinish: r.avgFinish,
        winningsMb: r.winningsMb,
        champion: r.championships > 0,
      }))
      .sort((a, b) => a.seasonNumber - b.seasonNumber);
  }

  const drivers = [...merged.values()].map((row) => {
    const seasons = (perSeason.get(row.driverId) ?? []).map((e) => e.row);
    return {
      ...row,
      seasons: seasons.length,
      avgStart: mergeAverage(seasons.map((s) => [s.avgStart, s.starts] as const)),
      avgFinish: mergeAverage(seasons.map((s) => [s.avgFinish, s.starts] as const)),
      bestFinish: minOf(seasons.map((s) => s.bestFinish)),
      worstFinish: maxOf(seasons.map((s) => s.worstFinish)),
      winPct: pct(row.wins, row.starts),
      top5Pct: pct(row.top5, row.starts),
      top10Pct: pct(row.top10, row.starts),
      finishPct: pct(row.racesFinished, row.starts),
      ledPct: pct(row.racesLed, row.starts),
      duelWinPct: pct(row.duelsWon, row.duelsWon + row.duelsLost),
      turnsLedPerStart: row.starts > 0 ? row.turnsLed / row.starts : null,
      // FRACTIONS, not percentages — see the note on `pct` above.
      rafPct: frac(row.racesFinished, row.starts),
      escapePct: frac(row.troubleEscapes, row.troubleAttempts),
      avgPointsPerStart: row.starts > 0 ? row.points / row.starts : 0,
      winningsPerStartMb: row.starts > 0 ? row.winningsMb / row.starts : null,
      // "Last week" is not a question the all-time table can answer.
      rankDelta: null,
      rankBefore: null,
      lastFive: [],
      top10Streak: 0,
      startStreak: 0,
      pointsByRace: {},
      byRace: {},
    };
  });

  drivers.sort((a, b) => b.points - a.points || b.wins - a.wins || a.driverId.localeCompare(b.driverId));
  const leaderPoints = drivers[0]?.points ?? 0;
  drivers.forEach((d, i) => {
    d.rank = i + 1;
    d.pointsBehind = leaderPoints - d.points;
    d.pointsBehindNext = i > 0 ? drivers[i - 1].points - d.points : 0;
  });

  return {
    seasonNumber: null,
    label: 'All time',
    complete: boards.every((b) => b.complete),
    throughRace: boards.reduce((a, b) => a + b.throughRace, 0),
    totalRaces: boards.reduce((a, b) => a + b.totalRaces, 0),
    championId: null,
    drivers,
    teams: allTimeTeams(boards),
    races: [],
    seasonNumbers: boards.map((b) => b.seasonNumber as number),
    perSeason: lines,
  };
}

/**
 * A SHOP ACROSS SEASONS IS KEYED BY ITS NAME, NOT ITS ID.
 *
 * `tm-01` is minted in charter order by `buildField` and is a different shop in
 * every league and, after a charter is sold (D-031), can be a different shop in
 * the same one. The NAME is the identity a person recognises — it is the same
 * choice `logoStore.ts` made for team marks, and for the same reason, which is
 * why a shop's logo and its career record agree about what a shop is.
 */
function allTimeTeams(boards: readonly StatBoard[]): TeamRow[] {
  const merged = new Map<string, TeamRow>();
  const parts = new Map<string, TeamRow[]>();
  for (const board of boards) {
    for (const team of board.teams) {
      const key = team.name.trim().toLowerCase();
      parts.set(key, [...(parts.get(key) ?? []), team]);
      const at = merged.get(key);
      merged.set(key, at ? addTeams(at, team) : { ...team, drivers: [...team.drivers] });
    }
  }
  const rows = [...merged.entries()].map(([key, row]) => {
    const seasons = parts.get(key) ?? [];
    return {
      ...row,
      seasons: seasons.length,
      avgStart: mergeAverage(seasons.map((s) => [s.avgStart, s.starts] as const)),
      avgFinish: mergeAverage(seasons.map((s) => [s.avgFinish, s.starts] as const)),
      bestFinish: minOf(seasons.map((s) => s.bestFinish)),
      winPct: pct(row.wins, row.starts),
      top5Pct: pct(row.top5, row.starts),
      finishPct: pct(row.racesFinished, row.starts),
      escapePct: pct(row.troubleEscapes, row.troubleAttempts),
      pointsPerStart: row.starts > 0 ? row.points / row.starts : null,
      rankDelta: null,
    };
  });
  rows.sort((a, b) => b.points - a.points || b.wins - a.wins);
  rows.forEach((r, i) => {
    r.rank = i + 1;
  });
  return rows;
}

function minOf(values: readonly (number | null)[]): number | null {
  const ok = values.filter((v): v is number => v !== null);
  return ok.length > 0 ? Math.min(...ok) : null;
}

function maxOf(values: readonly (number | null)[]): number | null {
  const ok = values.filter((v): v is number => v !== null);
  return ok.length > 0 ? Math.max(...ok) : null;
}

function addRows(a: StatRow, b: StatRow): StatRow {
  const causes: Record<string, number> = { ...a.dnfsByCause };
  for (const [k, v] of Object.entries(b.dnfsByCause)) causes[k] = (causes[k] ?? 0) + v;
  return {
    ...a,
    // The most recent season's name and ride win: a driver who changed shops
    // is listed under the one he drives for now, which is what a record book does.
    name: b.name,
    teamId: b.teamId,
    teamName: b.teamName,
    carNumber: b.carNumber || a.carNumber,
    championships: a.championships + b.championships,
    points: a.points + b.points,
    pointsForfeited: a.pointsForfeited + b.pointsForfeited,
    entries: a.entries + b.entries,
    starts: a.starts + b.starts,
    dnqs: a.dnqs + b.dnqs,
    missedTheShow: a.missedTheShow + b.missedTheShow,
    racesMissed: a.racesMissed + b.racesMissed,
    wins: a.wins + b.wins,
    poles: a.poles + b.poles,
    top5: a.top5 + b.top5,
    top10: a.top10 + b.top10,
    turnsLed: a.turnsLed + b.turnsLed,
    racesLed: a.racesLed + b.racesLed,
    mostTurnsLedRaces: a.mostTurnsLedRaces + b.mostTurnsLedRaces,
    racesFinished: a.racesFinished + b.racesFinished,
    dnfs: a.dnfs + b.dnfs,
    dnfsByCause: causes,
    troubleAttempts: a.troubleAttempts + b.troubleAttempts,
    troubleEscapes: a.troubleEscapes + b.troubleEscapes,
    troubleChipSaves: a.troubleChipSaves + b.troubleChipSaves,
    troubleCollected: a.troubleCollected + b.troubleCollected,
    duelsInstigated: a.duelsInstigated + b.duelsInstigated,
    duelsWon: a.duelsWon + b.duelsWon,
    duelsLost: a.duelsLost + b.duelsLost,
    heatIncidents: a.heatIncidents + b.heatIncidents,
    feudRaces: a.feudRaces + b.feudRaces,
    winningsMb: a.winningsMb + b.winningsMb,
    incomeMb: a.incomeMb + b.incomeMb,
    expenseMb: a.expenseMb + b.expenseMb,
    balanceMb: a.balanceMb + b.balanceMb,
  };
}

function addTeams(a: TeamRow, b: TeamRow): TeamRow {
  return {
    ...a,
    name: b.name,
    baseColor: b.baseColor ?? a.baseColor,
    driverIds: [...new Set([...a.driverIds, ...b.driverIds])],
    points: a.points + b.points,
    starts: a.starts + b.starts,
    entries: a.entries + b.entries,
    wins: a.wins + b.wins,
    poles: a.poles + b.poles,
    top5: a.top5 + b.top5,
    top10: a.top10 + b.top10,
    dnfs: a.dnfs + b.dnfs,
    racesFinished: a.racesFinished + b.racesFinished,
    turnsLed: a.turnsLed + b.turnsLed,
    racesLed: a.racesLed + b.racesLed,
    dnqs: a.dnqs + b.dnqs,
    heatIncidents: a.heatIncidents + b.heatIncidents,
    troubleAttempts: a.troubleAttempts + b.troubleAttempts,
    troubleEscapes: a.troubleEscapes + b.troubleEscapes,
    winningsMb: a.winningsMb + b.winningsMb,
    drivers: [...a.drivers, ...b.drivers],
  };
}

// ---------------------------------------------------------------------------
// The board for whatever the picker says
// ---------------------------------------------------------------------------

export type SeasonPick = number | 'all';

export function boardFor(sources: readonly StatsSource[], pick: SeasonPick): StatBoard | null {
  if (sources.length === 0) return null;
  if (pick === 'all') return allTimeBoard(sources);
  const found = sources.find((s) => s.seasonNumber === pick);
  return found ? seasonBoard(found) : seasonBoard(sources[sources.length - 1]);
}
